import { useState } from 'react';
import { DropResult } from 'react-beautiful-dnd';
import { useHistory } from 'react-router-dom';

import { HEADER_ID } from '@/components/templates/PageLayout';
import { ReactSingleSelectInput, SelectForm } from '@/components/uiParts';
import { useTargetsDomHeight } from '@/hooks/Dom';
import { redirect404Page } from '@/lib/AccessControl';
import { errorHandler } from '@/lib/ErrorHandler';
import { HttpError } from '@/lib/exception';
import { LocalStorageHandler } from '@/lib/LocalStorageHandler';
import { emptyStringToNull, nullToEmptyString } from '@/lib/util';
import { BUTTON_AREA_ID } from '@/pages/menu-managements/components/TopButtonArea';
import {
  OptionGroupDelivery,
  OptionGroupEatIn,
  OptionGroupForm,
  optionGroupForm,
  OptionGroupTakeout,
  Quantity,
} from '@/pages/menu-managements/option-groups/service';
import {
  OptionGroupFormError,
  validateLessMinItemLength,
  validateMemo,
  validateQuantityMax,
  validateQuantityMaxSameOption,
  validateQuantityMin,
  validateTitleJa,
} from '@/pages/menu-managements/option-groups/validate';
import {
  getSearchItems,
  SearchItem,
} from '@/pages/menu-managements/services/searchItem';
import { routes } from '@/router';
import { useAssociateBrandList } from '@/services/AssociateBrand/hooks/list/useAssociateBrandList';
import { useManagerAccountMe } from '@/services/AuthEnterprise/hooks';
import { useMenuManagementSearchOptionItems } from '@/services/BrandItem/hooks/SearchOptionItems/useMenuManagementSearchOptionItems';
import { useMenuManagementCreateBrandOptionGroup } from '@/services/BrandOptionGroup/hooks/create/useMenuManagementCreateBrandOptionGroup';
import { useMenuManagementBrandOptionGroupShow } from '@/services/BrandOptionGroup/hooks/show/useMenuManagementBrandOptionGroupShow';
import { useMenuManagementUpdateBrandOptionGroup } from '@/services/BrandOptionGroup/hooks/update/useMenuManagementUpdateBrandOptionGroup';
import { convertStringToNumber } from '@/validate/number';

type Return = {
  associateBrandSelectInput: ReactSingleSelectInput;
  titleJa: string;
  memo: string;
  takeout: OptionGroupTakeout;
  eatIn: OptionGroupEatIn;
  delivery: OptionGroupDelivery;
  optionGroupForm: OptionGroupForm;
  error: OptionGroupFormError;
  searchItems: SearchItem[];
  selectedItems: SearchItem[];
  searchItemName: string;
  isSearching: boolean;
  targetsHeight: number;
  initialStringifyFormState: string;
  quantity: Quantity;
  isLoading: boolean;
  isOptionGroupItemModalOpen: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  handleCreate: () => void;
  handleUpdate: (urlParamsId: string) => void;
  handleInputTitleJa: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleInputMemo: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handleInputTakeoutPosCode: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleInputEatInPosCode: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleInputDeliveryPosCode: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleOnDragEnd: (result: DropResult) => void;
  handleSearchItemNameInput: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleAddSelectItems: (item: SearchItem) => void;
  handleDeleteDragItem: (itemId: string) => void;
  handleInputQuantityMin: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleInputQuantityMax: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleInputQuantityMaxSameOption: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  initializeNewPageState: () => Promise<void>;
  initializeEditPageState: (urlParamsId: string) => Promise<void>;
  handleClickMultipleItemSelect: () => void;
  handleCloseOptionGroupItemModal: () => void;
  handleSetMultipleItems: (items: SearchItem[]) => void;
};

export const useMenuManagementOptionGroupForm = (): Return => {
  const history = useHistory();
  const { managerAccountMe } = useManagerAccountMe();
  /* アクセス制御 */
  redirect404Page('menu_management', history, managerAccountMe);
  const [timer, setTimer] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [titleJa, setTitleJa] = useState<string>('');
  const [quantity, setQuantity] = useState<Quantity>({
    min: 0,
    max: '',
    max_same_option: '',
  });
  const errorObj = { message: '', isError: false, isTouched: false };
  const [error, setError] = useState<OptionGroupFormError>({
    titleJa: { ...errorObj },
    memo: { ...errorObj },
    quantity: {
      min: { ...errorObj },
      max: { ...errorObj },
      maxSameOption: { ...errorObj },
    },
  });
  const [associateBrandSelectOptions, setAssociateBrandSelectOptions] =
    useState<SelectForm[]>([]);
  const [brandSelect, setBrandSelect] = useState<SelectForm | null>({
    label: '',
    value: '',
  });
  const [memo, setMemo] = useState<string>('');
  const [takeout, setTakeout] = useState<OptionGroupTakeout>({ pos_code: '' });
  const [eatIn, setEatIn] = useState<OptionGroupEatIn>({ pos_code: '' });
  const [delivery, setDelivery] = useState<OptionGroupDelivery>({
    pos_code: '',
  });
  const [, setIsDragged] = useState<boolean>(false);
  const [searchItemName, setSearchItemName] = useState<string>('');
  const [searchItems, setSearchItems] = useState<SearchItem[]>([]);
  const [selectedItems, setSelectedItems] = useState<SearchItem[]>([]);
  const [initialStringifyFormState, setInitialStringifyFormState] =
    useState<string>('');
  const [isOptionGroupItemModalOpen, setIsOptionGroupItemModalOpen] =
    useState<boolean>(false);

  const listUrl = routes.menuManagementOptionGroups.path;

  const handleInputTitleJa = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTitleJa = e.target.value;
    const newError = validateTitleJa(newTitleJa, error);
    setError(newError), setTitleJa(newTitleJa);
  };

  const handleInputMemo = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newMemo = e.target.value;
    const newError = validateMemo(newMemo, error);
    setError(newError), setMemo(newMemo);
  };

  const handleInputTakeoutPosCode = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setTakeout({ pos_code: e.target.value });
  };

  const handleInputEatInPosCode = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEatIn({ pos_code: e.target.value });
  };

  const handleInputDeliveryPosCode = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setDelivery({ pos_code: e.target.value });
  };

  const searchOptionItems = async (title: string) => {
    try {
      if (brandSelect === null) return;

      setIsSearching(true);
      const items = await useMenuManagementSearchOptionItems({
        associate_brand_id: brandSelect.value,
        title,
      });
      setSearchItems(items);
    } finally {
      setIsSearching(false);
    }
  };

  const handleClickMultipleItemSelect = () => {
    setIsOptionGroupItemModalOpen(true);
    searchOptionItems('');
  };

  const handleCloseOptionGroupItemModal = () =>
    setIsOptionGroupItemModalOpen(false);

  const handleSearchItemNameInput = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const inputName = e.target.value;
    setSearchItemName(inputName);
    clearTimeout(Number(timer));
    const newTimer = setTimeout(async () => {
      await searchOptionItems(inputName);
    }, 700);
    setTimer(Number(newTimer));
  };

  const validateItems = (itemsLength: number) => {
    const { isLessMinItemLength, error: newError } = validateLessMinItemLength(
      quantity.min,
      quantity.max_same_option,
      itemsLength,
      error
    );
    newError.quantity.min.isError = isLessMinItemLength;
    setError(newError);
  };

  const handleAddSelectItems = (item: SearchItem) => {
    setSearchItemName('');
    const items = [item, ...selectedItems];
    validateItems(items.length);
    setSelectedItems(items);
  };

  const handleSetMultipleItems = (items: SearchItem[]) => {
    validateItems(items.length);
    setSelectedItems(items);
  };

  const associateBrandSelectInput = {
    value: brandSelect,
    options: associateBrandSelectOptions,
    onChange: (newBrand: SelectForm | null) => {
      if (newBrand?.value === brandSelect?.value) return;
      setBrandSelect(newBrand);
      setSelectedItems([]);
    },
    onReset: () => {
      setBrandSelect({ value: '', label: '' });
    },
  };

  const handleOnDragEnd = (result: DropResult) => {
    const [reorderedItem] = selectedItems.splice(result.source.index, 1);
    selectedItems.splice(result.destination?.index ?? 0, 0, reorderedItem);
    setIsDragged((bool) => !bool);
    setSelectedItems(selectedItems);
  };

  const handleDeleteDragItem = (itemId: string) => {
    const newSelectedItems = selectedItems.filter((item) => item.id !== itemId);
    const { isLessMinItemLength, error: newError } = validateLessMinItemLength(
      quantity.min,
      quantity.max_same_option,
      newSelectedItems.length,
      error
    );
    newError.quantity.min.isError = isLessMinItemLength;
    setError(newError);
    setSelectedItems(newSelectedItems);
  };

  const handleInputQuantityMin = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { newValue, isNaN } = convertStringToNumber(e.target.value);
    if (!isNaN) {
      const newError = validateQuantityMin(
        { ...quantity, min: newValue },
        selectedItems.length,
        error
      );
      setError(newError), setQuantity({ ...quantity, min: newValue });
    }
  };

  const handleInputQuantityMax = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { newValue: newMax, isNaN } = convertStringToNumber(e.target.value);
    if (!isNaN) {
      if (newMax === 0) return;
      const newError = validateQuantityMax({ ...quantity, max: newMax }, error);
      setError(newError), setQuantity({ ...quantity, max: newMax });
    }
  };

  const handleInputQuantityMaxSameOption = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { newValue, isNaN } = convertStringToNumber(e.target.value);
    if (!isNaN) {
      const newError = validateQuantityMaxSameOption(
        { ...quantity, max_same_option: newValue },
        selectedItems.length,
        error
      );
      setError(newError);
      setQuantity({ ...quantity, max_same_option: newValue });
    }
  };

  const paramsQuantity = (() => {
    const { min, max, max_same_option } = quantity;
    return {
      min: min !== '' ? min : 0,
      max: max !== '' ? max : null,
      max_same_option: max_same_option !== '' ? max_same_option : null,
    };
  })();

  const handleCreate = () => {
    const { min } = quantity;
    if (min === '') return;
    (async () => {
      try {
        setIsLoading(true);
        await useMenuManagementCreateBrandOptionGroup({
          associate_brand_id: Number(associateBrandSelectInput.value?.value),
          title: {
            translation: {
              ja: titleJa,
            },
          },
          memo: emptyStringToNull(memo),
          takeout: {
            pos_code: emptyStringToNull(takeout.pos_code),
          },
          eat_in: {
            pos_code: emptyStringToNull(eatIn.pos_code),
          },
          delivery: {
            pos_code: emptyStringToNull(delivery.pos_code),
          },
          brand_item_sort_objects: getSearchItems(selectedItems),
          quantity: paramsQuantity,
        });
        setIsLoading(false);
        alert('オプショングループを登録しました。');
        history.push(listUrl);
      } catch (err) {
        setIsLoading(false);
        if (err instanceof HttpError && err.status === 400) {
          return alert(err.message);
        }
        // @ts-ignore TS2322
        errorHandler({ error: err });
      }
    })();
  };

  const handleUpdate = (urlParamsId: string) => {
    const { min } = quantity;
    if (min === '') return;
    (async () => {
      try {
        setIsLoading(true);
        await useMenuManagementUpdateBrandOptionGroup(urlParamsId, {
          associate_brand_id: Number(associateBrandSelectInput.value?.value),
          title: {
            translation: {
              ja: titleJa,
            },
          },
          memo: emptyStringToNull(memo),
          takeout: {
            pos_code: emptyStringToNull(takeout.pos_code),
          },
          eat_in: {
            pos_code: emptyStringToNull(eatIn.pos_code),
          },
          delivery: {
            pos_code: emptyStringToNull(delivery.pos_code),
          },
          brand_item_sort_objects: selectedItems,
          quantity: paramsQuantity,
        });
        setIsLoading(false);
        alert('オプショングループを更新しました。');
        history.push(listUrl);
      } catch (err) {
        setIsLoading(false);
        if (err instanceof HttpError && err.status === 400) {
          return alert(err.message);
        }
        // @ts-ignore TS2322
        errorHandler({ error: err });
      }
    })();
  };

  const { targetsHeight } = useTargetsDomHeight([HEADER_ID, BUTTON_AREA_ID]);

  const initializeNewPageState = async () => {
    const associateBrands = await useAssociateBrandList();
    const associateBrandOptions = associateBrands.map((brand) => {
      return {
        label: brand.name,
        value: brand.id.toString(),
      };
    });
    const { associateBrand } = LocalStorageHandler.get('menuManagement');
    const initialAssociateBrand =
      associateBrand !== undefined ? associateBrand : associateBrandOptions[0];
    setBrandSelect(initialAssociateBrand);
    LocalStorageHandler.set('menuManagement', {
      associateBrand: initialAssociateBrand,
    });
    setAssociateBrandSelectOptions(associateBrandOptions);
  };

  const initializeEditPageState = async (urlParamsId: string) => {
    const { associateBrand } = LocalStorageHandler.get('menuManagement');
    if (associateBrand === undefined) return history.push(listUrl);
    setBrandSelect(associateBrand);
    const optionGroup = await useMenuManagementBrandOptionGroupShow(
      urlParamsId
    );

    const titleja = optionGroup.title.translation.ja;
    const memo = nullToEmptyString(optionGroup.memo);
    const notNullableQuantity: Quantity = {
      min: optionGroup.quantity.min,
      max: optionGroup.quantity.max !== null ? optionGroup.quantity.max : '',
      max_same_option:
        optionGroup.quantity.max_same_option !== null
          ? optionGroup.quantity.max_same_option
          : '',
    };
    const selectedItems = optionGroup.brand_item_sort_objects;
    const takeout = {
      pos_code: optionGroup.takeout?.pos_code ?? '',
    };
    const eatIn = {
      pos_code: optionGroup.eat_in?.pos_code ?? '',
    };
    const delivery = {
      pos_code: optionGroup.delivery?.pos_code ?? '',
    };
    setTitleJa(titleja);
    setMemo(memo);
    setSelectedItems(selectedItems);
    setTakeout(takeout);
    setEatIn(eatIn);
    setDelivery(delivery);
    setQuantity(notNullableQuantity);
    setInitialStringifyFormState(
      JSON.stringify([
        titleja,
        memo,
        takeout,
        delivery,
        eatIn,
        selectedItems,
        notNullableQuantity,
      ])
    ); // WARNING 文字列比較なので順番を変更するときは注意
  };

  return {
    associateBrandSelectInput,
    titleJa,
    memo,
    takeout,
    eatIn,
    delivery,
    optionGroupForm,
    error,
    quantity,
    searchItems,
    selectedItems,
    searchItemName,
    isSearching,
    targetsHeight,
    initialStringifyFormState,
    isLoading,
    isOptionGroupItemModalOpen,
    setIsLoading,
    handleCreate,
    handleUpdate,
    handleInputTitleJa,
    handleInputMemo,
    handleInputTakeoutPosCode,
    handleInputEatInPosCode,
    handleInputDeliveryPosCode,
    handleOnDragEnd,
    handleSearchItemNameInput,
    handleAddSelectItems,
    handleDeleteDragItem,
    handleInputQuantityMin,
    handleInputQuantityMax,
    handleInputQuantityMaxSameOption,
    initializeNewPageState,
    initializeEditPageState,
    handleClickMultipleItemSelect,
    handleCloseOptionGroupItemModal,
    handleSetMultipleItems,
  };
};
